<template>
  <!-- Share: copy the board link. Present: switch to the clean, chrome-free view.

       Two buttons rather than one overloaded "share" that both copied and
       navigated. The old ShareButton copied the URL and then pushed to
       /Presentation, because getWishboardId() was a 1s mock that always resolved.
       Copying a link and then silently moving the person who clicked it to a
       different view conflated two unrelated intents, and made Presentation
       reachable only through a control labelled "share".

       They are separate now, and each says what it does:
         Share   copies /wishboard?wishboard_id=... -- the board, where a visitor
                 can actually add a wish. Presentation cannot receive wishes at
                 all: it has no composer and no Add a Wish button, only an Edit
                 button back. So it must never be the shared link.
         Present goes to /Presentation to view or project the board, and stays
                 local.
  -->
  <button @click="share" class="wb-action-btn"
          :class="{ 'is-confirmed': copied }"
          :title="copied ? 'Link copied' : 'Copy board link'"
          :aria-label="copied ? 'Link copied' : 'Copy board link'">
    <i class="bx" :class="copied ? 'bx-check' : 'bx-link-alt'"></i>
    <span>{{ copied ? 'Copied' : 'Share' }}</span>
  </button>

  <button @click="present" class="wb-action-btn"
          title="Present this board full screen"
          aria-label="Present this board full screen">
    <i class="bx bx-fullscreen"></i>
    <span class="desktop-only">Present</span>
  </button>
</template>

<script>
// Copies the board's own canonical URL -- never the current href, which would be
// wrong if this were ever rendered on /Presentation itself.
//
// navigator.clipboard is the supported path. document.execCommand('copy') is kept
// only as a fallback: it is deprecated, requires a user gesture, and fails
// silently on iOS Safari. Both paths report honestly -- a link someone believes was
// copied but was not is worse than a visible failure.
import { toast } from 'bulma-toast';

export default {
  name: 'ShareButton',
  data() {
    return {
      copied: false,
    };
  },
  beforeUnmount() {
    if (this._resetTimer) clearTimeout(this._resetTimer);
  },
  methods: {
    boardUrl() {
      const id = this.$route.query.wishboard_id;
      return window.location.origin + '/wishboard?wishboard_id=' + id;
    },

    async share() {
      // Read from the route, not from a 1s mock API call. The old code awaited
      // getWishboardId(), which always resolved to the same id -- so it worked,
      // just a second late for no reason.
      const url = this.boardUrl();
      const ok = await this.copyToClipboard(url);

      if (ok) {
        this.flashCopied();
      }
      toast({
        message: ok
          ? 'Board link copied. Paste it anywhere.'
          : 'Could not copy automatically — the link is in the address bar.',
        type: ok ? 'is-success' : 'is-warning',
        position: 'top-center',
        duration: 3000,
        dismissible: true,
        pauseOnHover: true,
      });
    },

    present() {
      const id = this.$route.query.wishboard_id;
      if (!id) return;
      this.$router.push({ path: '/Presentation', query: { wishboard_id: id } });
    },

    flashCopied() {
      this.copied = true;
      if (this._resetTimer) clearTimeout(this._resetTimer);
      this._resetTimer = setTimeout(() => { this.copied = false; }, 2000);
    },

    async copyToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (e) {
          // fall through to the legacy path
        }
      }

      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
      } catch (e) {
        return false;
      }
    },
  },
};
</script>

